import React, { useEffect, useMemo, useState } from 'react';
import {
  Building2, Edit, Plus, RefreshCw, Trash2, Settings, Search, X, Users,
  Briefcase, Globe2, Phone, MapPin, Upload, CalendarDays, Clock,
} from 'lucide-react';
import http from '../api/http';
import BlueHeader from '../components/BlueHeader';

/* ───────────── Styles partagés (palette neutre : slate + émeraude) ───────────── */

const inputCls =
  'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm ' +
  'placeholder:text-slate-400 transition focus:border-slate-400 focus:outline-none focus:ring-4 focus:ring-slate-900/5 ' +
  'disabled:bg-slate-50 disabled:text-slate-400 ' +
  'dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:placeholder:text-slate-500 dark:focus:border-slate-500 dark:focus:ring-white/10';

const btnPrimary =
  'inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-sm ' +
  'transition hover:bg-slate-800 focus:outline-none focus:ring-4 focus:ring-slate-900/20 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200';

const btnGhost =
  'inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 ' +
  'transition hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-900/5 ' +
  'dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700';

const iconBtn =
  'rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-300 ' +
  'dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-white';

const JOURS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

/* ───────────── Petits composants ───────────── */

const Field = ({ label, hint, span, children }) => (
  <div className={span ? 'sm:col-span-2' : ''}>
    <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">{label}</label>
    {children}
    {hint && <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">{hint}</p>}
  </div>
);

const TextField = ({ label, hint, span, value, onChange, ...rest }) => (
  <Field label={label} hint={hint} span={span}>
    <input className={inputCls} value={value ?? ''} onChange={(e) => onChange(e.target.value)} {...rest} />
  </Field>
);

const Modal = ({ title, subtitle, onClose, onSubmit, onConfirm, confirmLabel, children }) => {
  const Wrapper = onSubmit ? 'form' : 'div';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-4 dark:border-slate-800">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{title}</h3>
            {subtitle && <p className="text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} className={iconBtn} aria-label="Fermer">
            <X className="h-5 w-5" />
          </button>
        </div>
        <Wrapper onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 overflow-y-auto">{children}</div>
          <div className="flex justify-end gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-900/60">
            <button type="button" onClick={onClose} className={btnGhost}>Annuler</button>
            <button type={onSubmit ? 'submit' : 'button'} onClick={onConfirm} className={btnPrimary}>{confirmLabel}</button>
          </div>
        </Wrapper>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
      <Icon className="h-5 w-5" />
    </div>
    <div>
      <p className="text-2xl font-semibold leading-none text-slate-900 dark:text-white">{value}</p>
      <p className="mt-1.5 text-sm text-slate-500 dark:text-slate-400">{label}</p>
    </div>
  </div>
);

const ImageUpload = ({ label, preview, onChange, shape = 'square' }) => (
  <Field label={label}>
    <label className="group flex cursor-pointer items-center gap-4 rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-3 transition hover:border-slate-400 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800/40 dark:hover:border-slate-500">
      <div className={`flex shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800 ${shape === 'wide' ? 'h-14 w-20' : 'h-14 w-14'}`}>
        {preview ? (
          <img src={preview} alt={`Aperçu ${label}`} className="h-full w-full object-contain" />
        ) : (
          <Upload className="h-5 w-5 text-slate-400" />
        )}
      </div>
      <div className="text-sm">
        <p className="font-medium text-slate-700 dark:text-slate-200">{preview ? 'Changer l’image' : 'Choisir une image'}</p>
        <p className="text-xs text-slate-500 dark:text-slate-400">PNG, JPG ou SVG</p>
      </div>
      <input type="file" accept="image/*" onChange={onChange} className="sr-only" />
    </label>
  </Field>
);

const Switch = ({ checked, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition focus:outline-none focus:ring-4 focus:ring-slate-900/10 ${checked ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`}
  >
    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
  </button>
);

/* ───────────── Vue principale ───────────── */

const Filiales = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [flagFile, setFlagFile] = useState(null);
  const [flagPreview, setFlagPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configFiliale, setConfigFiliale] = useState(null);
  const [planningConfig, setPlanningConfig] = useState([]);
  const [joursFeries, setJoursFeries] = useState([]);
  const [deletedFeries, setDeletedFeries] = useState([]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await http.get('/api/filiales');
      setData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((i) =>
      [i.code, i.libelle, i.pays, i.ville].some((v) => (v || '').toLowerCase().includes(q))
    );
  }, [data, search]);

  const stats = useMemo(() => ({
    total: data.length,
    interim: data.filter((i) => i.traite_interim).length,
    personnel: data.reduce((sum, i) => sum + (Number(i.nombre_personnel) || 0), 0),
  }), [data]);

  /* ── CRUD filiale ── */

  const set = (key) => (value) => setFormData((f) => ({ ...f, [key]: value }));

  const handleCreate = () => {
    setEditingItem(null);
    setFormData({});
    setFlagFile(null); setFlagPreview(null);
    setLogoFile(null); setLogoPreview(null);
    setActiveTab('general');
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ ...item });
    setFlagFile(null); setFlagPreview(item.drapeau || null);
    setLogoFile(null); setLogoPreview(item.logo || null);
    setActiveTab('general');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette filiale ?')) return;
    try {
      await http.delete(`/api/filiales/${id}`);
      fetchData();
    } catch (error) {
      alert('Erreur lors de la suppression: ' + (error.response?.data?.error || 'Erreur inconnue'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const fd = new FormData();
      const f = formData;
      fd.append('code', f.code);
      fd.append('libelle', f.libelle);
      fd.append('pays', f.pays);
      fd.append('devise', f.devise);
      ['adresse', 'telephone', 'numero_securite_sociale', 'convention', 'raison_sociale',
        'representant', 'signataire_titre', 'signataire_rh_titre', 'signataire_rh_nom', 'ville', 'smic',
      ].forEach((k) => fd.append(k, f[k] || ''));
      fd.append('traite_interim', f.traite_interim !== undefined ? f.traite_interim : 0);
      if (flagFile) fd.append('drapeau', flagFile);
      if (logoFile) fd.append('logo', logoFile);

      const cfg = { headers: { 'Content-Type': 'multipart/form-data' } };
      if (editingItem) await http.put(`/api/filiales/${editingItem.id}`, fd, cfg);
      else await http.post('/api/filiales', fd, cfg);

      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error('Erreur soumission:', error);
      alert('Erreur: ' + (error.response?.data?.error || 'Erreur inconnue'));
    }
  };

  const pickImage = (setFile, setPreview) => (e) => {
    const file = e.target.files[0];
    if (file) { setFile(file); setPreview(URL.createObjectURL(file)); }
  };

  /* ── Configuration planning ── */

  const fid = configFiliale ? configFiliale.id || configFiliale.idfiliale : null;

  const handleConfig = async (filiale) => {
    try {
      const id = filiale.id || filiale.idfiliale;
      setConfigFiliale(filiale);
      const planningResponse = await http.get(`/api/planning-configuration/${id}`);
      const planningData = planningResponse.data || [];

      const full = [];
      for (let i = 1; i <= 7; i++) {
        full.push(
          planningData.find((p) => p.jour_semaine === i) || {
            filiale_id: id,
            jour_semaine: i,
            est_jour_travail: i <= 5 ? 1 : 0, // Lun-Ven ouvrés par défaut
            heure_debut: '08:00:00',
            heure_fin: '17:00:00',
          }
        );
      }
      setPlanningConfig(full);

      const feriesResponse = await http.get(`/api/jours-feries/${id}`);
      setJoursFeries(feriesResponse.data || []);
      setDeletedFeries([]);
      setShowConfigModal(true);
    } catch (error) {
      console.error('Erreur récupération configuration:', error);
      alert('Erreur lors de la récupération de la configuration');
    }
  };

  const handleConfigClose = () => {
    setShowConfigModal(false);
    setConfigFiliale(null);
    setPlanningConfig([]);
    setJoursFeries([]);
    setDeletedFeries([]);
  };

  const updateDay = (jour, patch) =>
    setPlanningConfig((prev) => prev.map((p) => (p.jour_semaine === jour ? { ...p, ...patch, filiale_id: fid } : p)));

  const updateFerie = (index, patch) =>
    setJoursFeries((prev) => prev.map((j, i) => (i === index ? { ...j, ...patch } : j)));

  const removeFerie = (index) => {
    const jour = joursFeries[index];
    if (jour.id) setDeletedFeries((d) => [...d, jour.id]);
    setJoursFeries((prev) => prev.filter((_, i) => i !== index));
  };

  const saveConfig = async () => {
    try {
      for (const config of planningConfig) {
        await http.post('/api/planning-configuration', {
          filiale_id: fid,
          jour_semaine: config.jour_semaine,
          est_jour_travail: config.est_jour_travail,
          heure_debut: config.heure_debut || '08:00:00',
          heure_fin: config.heure_fin || '17:00:00',
        });
      }
      for (const id of deletedFeries) await http.delete(`/api/jours-feries/${id}`);
      for (const jour of joursFeries) {
        if (jour.id) await http.put(`/api/jours-feries/${jour.id}`, jour);
        else if (jour.date && jour.nom) await http.post('/api/jours-feries', jour);
      }
      alert('Configuration sauvegardée avec succès');
      handleConfigClose();
    } catch (error) {
      console.error('Erreur sauvegarde configuration:', error);
      alert('Erreur lors de la sauvegarde de la configuration: ' + (error.response?.data?.error || error.message));
    }
  };

  /* ───────────── Rendu ───────────── */

  const tabs = [
    { id: 'general', label: 'Général' },
    { id: 'signataires', label: 'Signataires' },
    { id: 'rh', label: 'RH & Paie' },
  ];

  return (
    <div className="min-h-full space-y-6 bg-slate-50 p-6 dark:bg-slate-950">
      <BlueHeader
        title="Filiales"
        description="Gestion des filiales"
        icon={Building2}
        actions={[
          { label: 'Créer', icon: Plus, onClick: handleCreate, primary: true },
          { label: 'Actualiser', icon: RefreshCw, onClick: fetchData, primary: false },
        ]}
      />

      {/* Indicateurs */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard icon={Building2} label="Filiales" value={stats.total} />
        <StatCard icon={Briefcase} label="Traitent l’intérim" value={stats.interim} />
        <StatCard icon={Users} label="Collaborateurs au total" value={stats.personnel.toLocaleString('fr-FR')} />
      </div>

      {/* Liste */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-col gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            <span className="font-semibold text-slate-900 dark:text-white">{filtered.length}</span>{' '}
            {filtered.length > 1 ? 'filiales affichées' : 'filiale affichée'}
          </p>
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par nom, code, pays, ville"
              className={`${inputCls} pl-9`}
            />
          </div>
        </div>

        {loading ? (
          <div className="space-y-3 p-5">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-14 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-800" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center px-4 py-16 text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-slate-400 dark:bg-slate-800">
              <Building2 className="h-6 w-6" />
            </div>
            <p className="font-medium text-slate-900 dark:text-white">
              {search ? 'Aucun résultat' : 'Aucune filiale pour le moment'}
            </p>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {search ? 'Essayez un autre mot-clé.' : 'Créez votre première filiale pour commencer.'}
            </p>
            {!search && (
              <button type="button" onClick={handleCreate} className={`${btnPrimary} mt-5`}>
                <Plus className="h-4 w-4" /> Créer une filiale
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/70 text-left text-xs font-semibold text-slate-500 dark:border-slate-800 dark:bg-slate-800/40 dark:text-slate-400">
                  <th className="px-5 py-3">Filiale</th>
                  <th className="px-5 py-3">Localisation</th>
                  <th className="px-5 py-3">Téléphone</th>
                  <th className="px-5 py-3">Intérim</th>
                  <th className="px-5 py-3">Personnel</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {filtered.map((item) => (
                  <tr key={item.id} className="transition hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        {item.logo ? (
                          <img
                            src={item.logo}
                            alt={`Logo ${item.libelle}`}
                            className="h-11 w-11 shrink-0 rounded-lg border border-slate-200 bg-white object-contain p-0.5 dark:border-slate-700"
                          />
                        ) : (
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-400 dark:bg-slate-800">
                            <Building2 className="h-5 w-5" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">{item.libelle}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            <span className="rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300">{item.code}</span>
                            <span className="ml-2">#{item.idfiliale}</span>
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-700 dark:text-slate-300">
                      <div className="flex items-center gap-2">
                        {item.drapeau ? (
                          <img src={item.drapeau} alt="" className="h-4 w-6 rounded-sm border border-slate-200 object-cover dark:border-slate-700" />
                        ) : (
                          <Globe2 className="h-4 w-4 text-slate-400" />
                        )}
                        <span>{item.pays || '-'}</span>
                      </div>
                      {item.ville && (
                        <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                          <MapPin className="h-3 w-3" /> {item.ville}
                        </p>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-600 dark:text-slate-300">
                      {item.telephone ? (
                        <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-slate-400" />{item.telephone}</span>
                      ) : '-'}
                    </td>
                    <td className="px-5 py-3.5">
                      {item.traite_interim ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Oui
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                          <span className="h-1.5 w-1.5 rounded-full bg-slate-400" /> Non
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                        <Users className="h-3.5 w-3.5" /> {item.nombre_personnel || 0}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex justify-end gap-1">
                        <button type="button" onClick={() => handleConfig(item)} className={iconBtn} title="Configuration planning">
                          <Settings className="h-4 w-4" />
                        </button>
                        <button type="button" onClick={() => handleEdit(item)} className={iconBtn} title="Modifier">
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(item.id)}
                          className="rounded-lg p-2 text-slate-500 transition hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-200 dark:text-slate-400 dark:hover:bg-red-500/10 dark:hover:text-red-400"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal création / modification */}
      {showModal && (
        <Modal
          title={editingItem ? 'Modifier la filiale' : 'Créer une filiale'}
          subtitle={editingItem ? editingItem.libelle : 'Renseignez les informations de la nouvelle filiale'}
          onClose={() => setShowModal(false)}
          onSubmit={handleSubmit}
          confirmLabel={editingItem ? 'Enregistrer' : 'Créer'}
        >
          <div className="flex gap-1 border-b border-slate-200 px-6 dark:border-slate-800">
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveTab(t.id)}
                className={`-mb-px border-b-2 px-4 py-3 text-sm font-medium transition ${
                  activeTab === t.id
                    ? 'border-slate-900 text-slate-900 dark:border-white dark:text-white'
                    : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-5 p-6 sm:grid-cols-2">
            {activeTab === 'general' && (
              <>
                <TextField label="Code" value={formData.code} onChange={set('code')} required />
                <TextField label="Libellé" value={formData.libelle} onChange={set('libelle')} required />
                <TextField label="Pays" value={formData.pays} onChange={set('pays')} required placeholder="Ex: Bénin, Côte d'Ivoire, Cameroun..." />
                <TextField label="Ville" value={formData.ville} onChange={set('ville')} placeholder="Ex: Abidjan, Cotonou, Douala..." />
                <TextField label="Devise" value={formData.devise} onChange={set('devise')} placeholder="XOF, XAF..." />
                <TextField label="Raison sociale" value={formData.raison_sociale} onChange={set('raison_sociale')} placeholder="Raison sociale de l'entreprise" />
                <TextField span label="Adresse" value={formData.adresse} onChange={set('adresse')} placeholder="Adresse complète" />
                <TextField span label="Téléphone" value={formData.telephone} onChange={set('telephone')} placeholder="Numéro de téléphone" />
                <ImageUpload label="Logo" preview={logoPreview} onChange={pickImage(setLogoFile, setLogoPreview)} />
                <ImageUpload label="Drapeau" shape="wide" preview={flagPreview} onChange={pickImage(setFlagFile, setFlagPreview)} />
              </>
            )}

            {activeTab === 'signataires' && (
              <>
                <TextField span label="Représentant" value={formData.representant} onChange={set('representant')} placeholder="Nom du représentant légal" />
                <TextField label="Titre du signataire" value={formData.signataire_titre} onChange={set('signataire_titre')} placeholder="Ex: Directeur Général" />
                <TextField label="Titre du signataire RH" value={formData.signataire_rh_titre} onChange={set('signataire_rh_titre')} placeholder="Ex: Directeur des Ressources Humaines" />
                <TextField span label="Nom du signataire RH" value={formData.signataire_rh_nom} onChange={set('signataire_rh_nom')} placeholder="Nom du signataire RH" />
              </>
            )}

            {activeTab === 'rh' && (
              <>
                <TextField
                  label="SMIC" type="number" value={formData.smic} onChange={set('smic')}
                  placeholder="Salaire minimum interprofessionnel garanti"
                  hint="SMIC de la filiale (ex: 41494 pour le Bénin)"
                />
                <Field label="Traite l’intérim" hint="Indique si cette filiale calcule la paie de l'activité intérim">
                  <select
                    className={inputCls}
                    value={formData.traite_interim !== undefined ? formData.traite_interim : 0}
                    onChange={(e) => set('traite_interim')(parseInt(e.target.value, 10))}
                  >
                    <option value="0">Non</option>
                    <option value="1">Oui</option>
                  </select>
                </Field>
                <TextField label="N° Sécurité sociale" value={formData.numero_securite_sociale} onChange={set('numero_securite_sociale')} placeholder="Numéro de sécurité sociale" />
                <TextField label="Convention" value={formData.convention} onChange={set('convention')} placeholder="Convention collective" />
              </>
            )}
          </div>
        </Modal>
      )}

      {/* Modal configuration planning */}
      {showConfigModal && configFiliale && (
        <Modal
          title="Configuration du planning"
          subtitle={configFiliale.libelle}
          onClose={handleConfigClose}
          onConfirm={saveConfig}
          confirmLabel="Sauvegarder"
        >
          <div className="space-y-8 p-6">
            <section>
              <h4 className="mb-3 flex items-center gap-2 text-base font-semibold text-slate-900 dark:text-white">
                <Clock className="h-4 w-4 text-slate-400" /> Jours de travail
              </h4>
              <div className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 dark:divide-slate-800 dark:border-slate-800">
                {JOURS.map((jour, index) => {
                  const cfg = planningConfig.find((p) => p.jour_semaine === index + 1);
                  const worked = cfg?.est_jour_travail === 1;
                  return (
                    <div key={jour} className="flex flex-wrap items-center gap-4 px-4 py-3">
                      <Switch checked={worked} label={`${jour} ouvré`} onChange={(v) => updateDay(index + 1, { est_jour_travail: v ? 1 : 0 })} />
                      <div className="w-28">
                        <p className="text-sm font-medium text-slate-900 dark:text-white">{jour}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{worked ? 'Ouvré' : 'Non ouvré'}</p>
                      </div>
                      <div className="ml-auto flex items-center gap-2">
                        <input
                          type="time" disabled={!worked} title="Heure de début"
                          value={cfg?.heure_debut?.substring(0, 5) || '08:00'}
                          onChange={(e) => updateDay(index + 1, { heure_debut: e.target.value + ':00' })}
                          className={`${inputCls} w-28`}
                        />
                        <span className="text-slate-400">à</span>
                        <input
                          type="time" disabled={!worked} title="Heure de fin"
                          value={cfg?.heure_fin?.substring(0, 5) || '17:00'}
                          onChange={(e) => updateDay(index + 1, { heure_fin: e.target.value + ':00' })}
                          className={`${inputCls} w-28`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            <section>
              <div className="mb-3 flex items-center justify-between">
                <h4 className="flex items-center gap-2 text-base font-semibold text-slate-900 dark:text-white">
                  <CalendarDays className="h-4 w-4 text-slate-400" /> Jours fériés de l’année
                </h4>
                <button
                  type="button"
                  onClick={() => setJoursFeries([...joursFeries, { id: null, filiale_id: fid, date: '', nom: '', description: '' }])}
                  className={btnGhost}
                >
                  <Plus className="h-4 w-4" /> Ajouter un jour férié
                </button>
              </div>

              {joursFeries.length === 0 ? (
                <p className="rounded-xl border border-dashed border-slate-300 py-8 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
                  Aucun jour férié défini pour cette filiale.
                </p>
              ) : (
                <div className="space-y-2">
                  {joursFeries.map((jour, index) => (
                    <div key={jour.id ?? `new-${index}`} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-2.5 dark:border-slate-800 dark:bg-slate-800/40">
                      <input
                        type="date" value={jour.date}
                        onChange={(e) => updateFerie(index, { date: e.target.value })}
                        className={`${inputCls} w-44`}
                      />
                      <input
                        type="text" value={jour.nom} placeholder="Nom du jour férié"
                        onChange={(e) => updateFerie(index, { nom: e.target.value })}
                        className={`${inputCls} flex-1`}
                      />
                      <button
                        type="button" onClick={() => removeFerie(index)} title="Supprimer"
                        className="rounded-lg p-2 text-slate-500 transition hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10 dark:hover:text-red-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Filiales;